/**
 * CustomMarcasCasaA — a marca da Casa A em vetor: o NOME e o SÍMBOLO.
 *
 * ORIGEM. Nenhum dos dois é traçado a mão: são os paths originais do Figma.
 * O nome ("CASAA") saiu do `background_2.svg` dos relatórios de Power BI
 * (`D:\WorkFolder\Data\PowerBi Reports\reports_casaa\base`); o símbolo (o Λ com
 * o ponto) saiu do `A com ponto.svg` que o Vittorio entregou em 26/08/2026, e
 * está guardado como fonte em `.claude/data/input/marca/A-com-ponto.svg`.
 *
 * ⚠️ **NENHUM DOS DOIS TEM COR PRÓPRIA, e é a razão de existirem.**
 * `fill="currentColor"`: a marca herda a cor do texto de quem a contém. No
 * header quem manda é o `--adminuiux-text` do tema, que já vira branco sozinho
 * em header colorido e no modo escuro. Logo: **não escreva regra de cor para
 * estas marcas** — se aparecerem da cor errada, quem está errado é a cor do
 * texto em volta.
 *
 * Antes disto a marca eram QUATRO imagens chapadas: `casaa-wordmark.png` (preto),
 * `casaa-wordmark-light.png` (branco), um `filter: invert()` para o modo escuro,
 * e o `casaa-icon.png` — um círculo preto que continuava preto sobre o header
 * azul, porque PNG não obedece a tema nenhum.
 */
import React from 'react';

interface Props {
  /**
   * Altura em px — **exceção, não o caminho normal.**
   *
   * O tamanho da marca é CSS, e CSS mora na folha: só lá ele pode mudar por
   * media query, acompanhar o `rem` de quem aumentou a fonte do sistema, e ser
   * achado por quem procura "de onde vem esse tamanho". Um número escrito no
   * `.tsx` é o mesmo em qualquer tela, para sempre.
   *
   * Sem esta prop, o SVG não emite `width`/`height` e o tamanho vem da classe —
   * é assim que o header e o login usam. Passe um número só em bancada, teste ou
   * lugar sem folha de estilo.
   */
  altura?: number;
  className?: string;
}

/* Sem `altura`, nenhum atributo de dimensão é emitido: quem manda é o CSS.
   `undefined` no JSX faz o React OMITIR o atributo, que é exatamente o que se
   quer — `height="0"` ou `height=""` teriam outro efeito. */
const dimensoes = (altura: number | undefined, razao: number) =>
  altura === undefined
    ? {}
    : { height: altura, width: Number((altura * razao).toFixed(2)) };

/* ─────────────────────────── O NOME ─────────────────────────── */

/* Da caixa medida por `getBBox`, não estimada: 300 / 68. */
const RAZAO_NOME = 300 / 68;

/**
 * O wordmark "CASAA" — a palavra, com o ponto no último A.
 * Passe só a `altura`; a largura é calculada.
 */
export const CustomMarcaCasaANome: React.FC<Props> = ({ altura, className }) => (
  <svg
    viewBox="70 59 300 68"
    {...dimensoes(altura, RAZAO_NOME)}
    fill="currentColor"
    className={className}
    role="img"
    aria-label="Casa A"
  >
    {/* C */}
    <path d="M134.341 111.281C132.86 113.528 131.065 115.573 128.958 117.416C126.85 119.202 124.486 120.729 121.866 121.996C119.245 123.206 116.426 124.157 113.406 124.848C110.387 125.539 107.254 125.885 104.007 125.885C99.3364 125.885 94.9217 124.992 90.7633 123.206C86.6619 121.363 83.0732 118.885 79.9972 115.774C76.9211 112.664 74.4717 109.034 72.6488 104.886C70.8829 100.681 70 96.2159 70 91.4919C70 87.8049 70.5412 84.2619 71.6235 80.8629C72.7058 77.4639 74.2438 74.353 76.2375 71.5301C78.2313 68.7073 80.6238 66.23 83.415 64.0985C86.2062 61.9093 89.3107 60.2098 92.7286 59L94.352 64.617C91.5608 65.6539 89.0259 67.0654 86.7474 68.8513C84.4688 70.6372 82.5036 72.7111 80.8516 75.0731C79.2566 77.3775 78.0034 79.9412 77.092 82.764C76.1806 85.5293 75.7249 88.4386 75.7249 91.4919C75.7249 95.4094 76.4654 99.1252 77.9465 102.639C79.4275 106.096 81.4497 109.121 84.0131 111.713C86.5765 114.305 89.5671 116.351 92.9849 117.848C96.4597 119.346 100.134 120.095 104.007 120.095C109.533 120.095 114.546 119.029 119.046 116.898C123.603 114.766 127.078 111.828 129.47 108.084L134.341 111.281Z" />
    {/* A */}
    <path d="M191.854 125.885L167.16 72.0486L142.295 125.885H135.972L166.647 59.2592H167.673L198.006 125.885H191.854Z" />
    {/* S */}
    <path d="M232.691 97.8866C234.685 100.767 235.682 103.993 235.682 107.565C235.682 112.75 233.973 117.099 230.555 120.614C227.081 124.128 222.78 125.885 217.653 125.885C214.121 125.885 210.789 125.107 207.656 123.552C204.808 122.054 202.387 120.124 200.393 117.762L204.067 113.355C208.225 117.906 212.64 120.182 217.311 120.182C220.843 120.182 223.862 118.972 226.368 116.552C228.932 114.133 230.214 111.252 230.214 107.911C230.214 105.203 229.359 102.812 227.65 100.738C226.112 98.722 222.58 96.2447 217.055 93.3066C211.529 90.3685 207.77 87.4592 205.776 84.5788C203.839 81.7559 202.871 78.5585 202.871 74.9867C202.871 70.2627 204.409 66.3453 207.485 63.2343C209.251 61.4484 211.387 60.1522 213.893 59.3457L215.175 64.7898C213.751 65.3083 212.412 66.1436 211.159 67.2958C208.994 69.3698 207.912 71.7894 207.912 74.5546C207.912 76.9166 208.795 79.221 210.561 81.4678C212.213 83.5994 215.602 86.019 220.729 88.7267C226.596 91.8952 230.584 94.9485 232.691 97.8866Z" />
    {/* A */}
    <path d="M292.735 125.885L268.041 72.0486L243.176 125.885H236.853L267.529 59.2592H268.554L298.887 125.885H292.735Z" />
    {/* A */}
    <path d="M363.848 127L339.154 73.1636L314.289 127H307.966L338.641 60.3743H339.667L370 127H363.848Z" />
    {/* o ponto do último A */}
    <path d="M345.91 106.372C345.91 110.067 342.948 113.062 339.295 113.062C335.641 113.062 332.68 110.067 332.68 106.372C332.68 102.677 335.641 99.6818 339.295 99.6818C342.948 99.6818 345.91 102.677 345.91 106.372Z" />
  </svg>
);

/* ────────────────────────── O SÍMBOLO ────────────────────────── */

/**
 * O símbolo — o Λ com o ponto, dentro da bola. Quadrado: largura = altura.
 *
 * ⚠️ **O DESENHO É VAZADO, NÃO PINTADO POR CIMA.** É UM path só, com
 * `fill-rule="evenodd"`: a bola é `currentColor` e o Λ mais o ponto são
 * BURACOS nela, por onde passa o fundo real de quem está atrás. Daí o símbolo
 * contrastar por construção — sobre o header azul a bola fica branca e o Λ,
 * azul; no fundo claro a bola fica escura e o Λ, claro. Uma peça, uma cor,
 * nunca uma combinação impossível.
 *
 * A alternativa era declarar duas cores (bola numa, Λ noutra), e ela inventa
 * uma peça de cor que a lei visual só autoriza no segundo uso.
 *
 * GEOMETRIA, medida e não estimada: os dois paths são os do arquivo original,
 * INTACTOS. Quem se ajustou a eles foi a bola — o `casaa-icon.png` (144×144)
 * foi medido pixel a pixel e o desenho ocupa 59,7% da largura do círculo,
 * centrado. Daí o raio 161 no espaço original do SVG (193,1 / 0,597 / 2) e o
 * centro em (96,55 / 102,53), que é o centro da caixa do Λ.
 */
export const CustomMarcaCasaASimbolo: React.FC<Props> = ({ altura, className }) => (
  <svg
    viewBox="-64.45 -58.47 322 322"
    {...dimensoes(altura, 1)}
    fill="currentColor"
    className={className}
    role="img"
    aria-label="Casa A"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d={
        /* a bola */
        'M96.55 -58.47A161 161 0 1 0 96.55 263.53A161 161 0 1 0 96.55 -58.47Z' +
        /* o Λ, vazado */
        'M173.948 205.068L97.0813 39.3645L19.6822 205.068H0L95.4854 0H98.6772L193.099 205.068H173.948Z' +
        /* o ponto, vazado */
        'M97.5189 120.9863A20.5917 20.5917 0 1 0 97.5189 162.1697A20.5917 20.5917 0 1 0 97.5189 120.9863Z'
      }
    />
  </svg>
);
